import React, { useEffect, useContext, useState, useMemo } from 'react';
import {
  arrayOf as propTypeArrayOf,
  shape as shapeProp,
  string as propTypeString,
  number as propTypeNumber
} from 'prop-types';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { BuyboxActions } from '@thd-olt-component-react/buybox/dist/component/subcomponents/BuyboxActions';
import { BuyboxMessage } from '@thd-olt-component-react/buybox/dist/component/subcomponents/BuyboxMessage';
import {
  ExcludedShipState
} from '@thd-olt-component-react/fulfillment/dist/components/tiles/ExcludedShipState/ExcludedShipState';
import {
  params, string, arrayOf, bool, shape, extend
} from '@thd-nucleus/data-sources';
import { isPaypalEligible } from '@thd-olt-component-react/buybox/dist/BuyboxHelper';
import { Buybox } from '@thd-olt-component-react/buybox';
import BuyboxContainer from './subcomponents/BuyboxContainer';
import { MissingProductToaster } from './subcomponents/productUnavailable/MissingProductToaster';
import useProductsFullfilmentValidation from '../hooks/useProductsFullfilmentValidation';
import useGetExcludedShipState from '../hooks/useGetExcludedShipState';
import useGetCartItems from '../hooks/useGetCartItems';
import {
  bundleAddToCart, filterRemovedProducts, getB2BUser, getB2CUser
  , getBundleFulfillment, getPaypalCartItems, getProductsDefaultValue, isReturnMessagesEligible,
  getBundleQuantityLimit, hasMissingProducts, combineBundleProducts, expandAccessoryQuantities
} from '../utils/product-bundle-utils';
import { BundleFulfillment } from './BundleFulfillment';
import { isBundleUnavailable } from '../utils/product-unavailable-utils';
import { OUT_OF_STOCK } from '../constants/index';
import '../styles/BundleBuybox.scss';
import { DeliveryInfo } from './subcomponents/DeliveryInfo';
import { BundleQuantityMessaging } from './subcomponents/BundleQuantityMessaging';
/**
 * Props
 *
 * @typedef {object} Props
 * @property {Product[]} products Array of products
 * @property {String} itemId parent itemid
 */

/**
 * Returns a component, BundleSummary, showing the total price and
 * discounts applied to the bundle.
 * @param {Props} Props
 * @returns {React.ReactElement} JSX
 */

export const BundleBuybox = ({
  product, products, itemId, pricingDetails, selectedAccessories
}) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('bundle-buybox.ready');
  }, []);

  const {
    quantity,
    paypalCheckout,
    addToCart,
    deliveryInfo,
    deliveryExpress,
    showQuantityLimit,
    showUnavailableMessage,
    displayMissingProductsToaster,
    disableATCForMissingProducts,
    hideBuybox,
    enableFulfillmentTiles
  } = product?.features ?? {};

  const maxQuantityLimit = pricingDetails?.maxQuantityLimit;

  const isBipFulfillmentTilesEnabled = useConfigService('isBipFulfillmentTilesEnabled');
  const normalizedAccessories = expandAccessoryQuantities(selectedAccessories);
  const bundleProducts = combineBundleProducts(products, normalizedAccessories);
  const activeProducts = useMemo(() => filterRemovedProducts(bundleProducts), [products, selectedAccessories]);
  const [closeMissingProductToaster, setCloseMissingProductToaster] = useState(false);
  const experienceContext = useContext(ExperienceContext);
  const { store, isConsumerApp } = experienceContext;
  const storeId = store?.storeId;
  const storeZip = store?.storeZip;
  const deliveryZip = experienceContext?.deliveryZip;
  const bundleId = product?.itemId;
  const [bundleQuantity, setBundleQuantity] = useState(1);
  const experienceType = product?.bundleSpecificationDetails?.experienceType;
  const isUnavailable = hideBuybox && isBundleUnavailable({ products });
  const bundleType = product?.bundleSpecificationDetails?.type;

  const {
    disableATC,
    productsFulfillmentInformation
  } = useProductsFullfilmentValidation({
    products: activeProducts,
    deliveryZip,
    bundleQuantity,
    storeZip,
    quantityLimitInventory: maxQuantityLimit?.quantityLimitWithInventory,
    addToCartFlag: addToCart,
    disableATCForMissingProductsFlag: disableATCForMissingProducts
  });
  const { cartItems } = useGetCartItems({
    productsFulfillmentInformation, bundleQuantity, storeId, deliveryZip, storeZip, bundleId, experienceType, bundleType
  });
  const {
    excludedShipState,
    showCheckAvailability,
    setShowCheckAvailability
  } = useGetExcludedShipState({ products: activeProducts, deliveryZip, storeZip });

  const onQuantityChange = (event) => setBundleQuantity(Number(event?.quantity));
  const bundleAtcClickPassThrough = () => bundleAddToCart({
    cartItems, storeId, storeZip, zipCode: deliveryZip, product
  });

  const isPaypalB2BEnabled = useConfigService('PAYPAL_ENABLE_B2B');
  const paypalEligible = isPaypalEligible(getB2BUser(), getB2CUser(), isPaypalB2BEnabled);
  const showPaypalSmartButton = useConfigService('PAYPAL_ENABLE');

  const isRisMessageEnabled = useConfigService('fs:isRisMessageEnabled');
  const isReturnMessageEligible = isReturnMessagesEligible(activeProducts);
  const quantityLimit = getBundleQuantityLimit(maxQuantityLimit, showQuantityLimit);

  const { value } = getProductsDefaultValue(activeProducts);

  const fulfillmentMethod = cartItems?.[0]?.fulfillmentMethod;
  const isIncompleteBundle = hasMissingProducts(products);
  const onCloseMissingProductToaster = () => {
    setCloseMissingProductToaster(true);
  };
  const paypalCartItems = useMemo(() => {
    return getPaypalCartItems({ cartItems, deliveryZip });
  }, [cartItems]);

  const fulfillmentOptions = useMemo(() => {
    return getBundleFulfillment({ products: activeProducts });
  }, [activeProducts]);

  if (isUnavailable) {
    return null;
  }

  if (showUnavailableMessage) {
    if (excludedShipState?.isExcluded) {
      return (
        <BuyboxContainer>
          <ExcludedShipState
            stateText={excludedShipState?.storeStateName}
            itemId={itemId}
            showCheckAvailability={showCheckAvailability}
            setShowCheckAvailability={setShowCheckAvailability}
          />
        </BuyboxContainer>
      );
    }
  }

  const buyboxWrapperClass = 'buybox-wrapper' + (!quantity ? ' buybox-hide-quantity' : '');
  const bundleRisMessage = 'This bundle is only returnable in store.';
  const showBundleRisMessage = isRisMessageEnabled && isReturnMessageEligible;
  const hidePaypalCheckout = !paypalCheckout || !showPaypalSmartButton;
  const showMissingProductToaster = displayMissingProductsToaster && isIncompleteBundle && !closeMissingProductToaster;

  const isExchangeCustomer = experienceContext?.customer?.thd_storefront?.storeFrontType === 'EXCHANGE';
  const renderFulfillmentTiles = enableFulfillmentTiles && isBipFulfillmentTilesEnabled && !isExchangeCustomer;

  return (
    <BuyboxContainer>
      {!renderFulfillmentTiles && showBundleRisMessage && (
        <div data-testid="return-messaging">
          <BuyboxMessage message={bundleRisMessage} status="info" />
        </div>
      )}
      <div className={buyboxWrapperClass} data-testid="buybox-wrapper-buybox-actions">
        {!renderFulfillmentTiles && showQuantityLimit && (
          <BundleQuantityMessaging
            maxQuantity={maxQuantityLimit?.quantityLimit}
            quantity={bundleQuantity}
          />
        )}
        {renderFulfillmentTiles ? (
          <Buybox
            itemId={itemId}
            customRisMessage={showBundleRisMessage ? bundleRisMessage : ''}
            sticky={!isConsumerApp}
            hideInstantCheckout
            hidePaypalCheckout={hidePaypalCheckout}
            quantityLimitOverride={maxQuantityLimit?.quantityLimit}
          />
        ) : (
          <BuyboxActions
            itemId={itemId}
            configurableItems={cartItems}
            onQuantityChange={onQuantityChange}
            quantity={bundleQuantity}
            onClickPassThrough={bundleAtcClickPassThrough}
            paypalEligible={paypalEligible}
            hideInstantCheckout
            sticky={!isConsumerApp}
            hideQuantity={!quantity}
            hidePaypalCheckout={hidePaypalCheckout}
            disableATC={disableATC}
            fulfillment={fulfillmentOptions}
            bipCartItems={paypalCartItems}
            itemPrice={value}
            fulfillmentMethod={fulfillmentMethod}
            quantityLimit={quantityLimit}
          />
        )}
        {showMissingProductToaster && (
          <MissingProductToaster onClose={onCloseMissingProductToaster} product={product} />
        )}
        {!renderFulfillmentTiles && (
          <div className="sui-mt-6">
            {
              deliveryInfo
                ? <DeliveryInfo deliveryExpress={deliveryExpress} />
                : <BundleFulfillment products={activeProducts} />
            }
          </div>
        )}
      </div>
    </BuyboxContainer>
  );
};

BundleBuybox.propTypes = {
  product: shapeProp({
    bundleSpecificationDetails: shapeProp({
      type: propTypeString
    })
  }).isRequired,
  products: propTypeArrayOf(
    shapeProp({
      fulfillment: shapeProp(),
      info: shapeProp()
    })
  ).isRequired,
  itemId: propTypeString.isRequired,
  pricingDetails: shapeProp({
    maxQuantityLimit: propTypeNumber
  }),
  selectedAccessories: propTypeArrayOf(shapeProp({})).isRequired
};

BundleBuybox.defaultProps = {
  pricingDetails: null
};

BundleBuybox.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      bundleSpecificationDetails: params({ storeId: string(), zipCode: string() }).shape({
        type: string()
      })
    })
  },
  {
    products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
      info: shape({
        quantityLimit: string(),
        isInStoreReturnMessageEligible: bool(),
        returnable: string()
      }),
      fulfillment: {
        anchorStoreStatusType: string(),
        backordered: bool(),
        backorderedShipDate: string(),
        bossExcludedShipStates: string(),
        bodfsAssemblyEligible: bool(),
        seasonStatusEligible: bool(),
        excludedShipStates: string(),
        fulfillmentOptions: shape({
          fulfillable: bool(),
          services: shape({
            type: string(),
            isDefault: bool(),
            deliveryCharge: string(),
            deliveryDates: shape({
              endDate: string(),
              startDate: string()
            }),
            deliveryTimeline: string(),
            freeDeliveryThreshold: string(),
            totalCharge: string(),
            locations: shape({
              type: string(),
              isAnchor: bool(),
              locationId: string(),
              storeName: string(),
              storePhone: string(),
              state: string(),
              distance: string(),
              inventory: shape({
                quantity: string(),
                isInStock: bool(),
                isOutOfStock: bool(),
                isLimitedQuantity: bool(),
                isUnavailable: bool(),
                maxAllowedBopisQty: string(),
                minAllowedBopisQty: string()
              })
            })
          }),
          type: string()
        })
      }
    })
  },
  Buybox
);

BundleBuybox.displayName = 'BundleBuybox';
